<template>
  <vs-form v-if="!isFetching" ref="form">
    <vs-wrapper>
      <RepeatJobsInfoDisplay
        class="mt-1"
        :job-id="jobUuid"
        :job-request-id="jobRequestId"
        :enable-request-sidesheet="
          activity?.stopConfirmationRequirement === StopConfirmationRequirement.Required
        "
      />
      <PrioritySelector v-if="!isDisabled" :selected="priorityId" @on-update="updatePriority" />

      <vs-heading type="overline">Who's it for</vs-heading>
      <vs-wrapper space="small">
        <div class="grid">
          <div class="grid-item">
            <JobDivisionPicker
              data-test="division-selector"
              :value="ownerId"
              :job-hub-id="hubId"
              :is-disabled="isDisabled || !isPrimaryJobOfJobRequest"
              @input="$emit('update:ownerId', $event)"
              @autofill="(field, value) => autoFill(field, value)"
            />
          </div>

          <div class="grid-item">
            <JobRequesterPicker
              :customer-sid="customerSid"
              :is-disabled="isDisabled || !isPrimaryJobOfJobRequest"
              :job-hub-id="hubId"
              @selected="$emit('update:customerSid', $event)"
              @autofill="(field, value) => autoFill(field, value)"
            />
          </div>
        </div>

        <job-followers-picker
          :follower-ids="followerIds"
          :follower-sids-by-id="followerSidsById"
          :is-disabled="isDisabled"
          :job-hub-id="hubId"
          @selected="$emit('update:followerIds', $event)"
        />
      </vs-wrapper>
    </vs-wrapper>

    <task-item
      ref="task-item"
      :key="jobId"
      :unit-id="unitId"
      :service-provider-business-unit-id="serviceProviderBusinessUnitId"
      :job-status="jobStatus"
      :status="unitWorkingStatus"
      :active-duration="activeDuration"
      :activity-id="activityId"
      :resource-type-id="resourceTypeId"
      :crew="crew"
      :is-disabled="isDisabled"
      :is-primary-job-of-job-request="isPrimaryJobOfJobRequest"
      :job-request-is-repeat-trip="jobRequestIsRepeatTrip"
      :hub-id="hubId"
      :account-id="accountId"
      :assignment-group-number="assignmentGroupNumber"
      :pool-id="poolId"
      :pool-definition-id="poolDefinitionId"
      :is-creating-ad-hoc-pool="isCreatingAdHocPool"
      :job-number="jobId"
      :job-id="jobUuid"
      :job-request-id="jobRequestId"
      :show-repeated-job-warning-banner="showRepeatedJobWarningBanner"
      @remove-job-from-group="removeJobFromGroup"
      @update-unit="updateUnit"
      @update-service-provider-business-unit-id="updateServiceProviderBusinessUnitId"
      @update-activity="updateActivity"
      @update-crew="updateCrew"
      @auto-fill="autoFill"
      @update-pool="assignToPool"
      @create-ad-hoc-pool="createAdHocPool"
    >
      <template #assigned>
        <pool-status
          v-if="jobStatus === 'AssignedToPool' && poolId"
          :pool-id="poolId"
          @status:closed="onPoolClosed"
        />
        <ad-hoc-pool
          v-else-if="poolDefinitionId"
          :definition-id="poolDefinitionId"
          :hub-id="hubId"
          :resource-type-id="activity.resourceTypeId"
          :itineraries="itineraries"
          @input="updatePoolExecutionPlan"
        />
        <ad-hoc-pool-creator
          v-else-if="isCreatingAdHocPool"
          :hub-id="hubId"
          :resource-type-id="activity.resourceTypeId"
          :ad-hoc-pool="adHocPool"
          @add-stage="addAdHocPoolStage"
          @update-stage="updateAdHocPoolStage"
          @remove-stage="removeAdHocPoolStage"
          @update-delay="updateAdHocPoolDelay"
        />
      </template>
    </task-item>

    <v-row no-gutters align="center">
      <v-col>
        <vs-heading type="overline">When?</vs-heading>
      </v-col>
      <v-col v-if="showBulkCreateIcon" class="shrink">
        <div>
          <v-tooltip top nudge-left="60">
            <template #activator="{ on }">
              <div v-on="on">
                <vs-button
                  :disabled="cannotCloseBulkCreate || cannotOpenBulkCreate"
                  data-test="bulk-create"
                  :icon="bulkCreate ? 'today' : 'date_range'"
                  type="tertiary"
                  small
                  @click="toggleBulkCreate"
                />
              </div>
            </template>
            <span v-if="bulkCreate && !cannotCloseBulkCreate">Create single job</span>
            <span v-else-if="cannotCloseBulkCreate">Clear job selection to create single job</span>
            <span v-else-if="cannotOpenBulkCreate">Clear attachments to create multiple jobs</span>
            <span v-else>Create multiple jobs</span>
          </v-tooltip>
        </div>
      </v-col>
    </v-row>

    <vs-wrapper space="small">
      <ticket-form-multi-day-scheduler
        v-if="bulkCreate"
        :start-time="startTime"
        :end-time="endTime"
        :time-slot-dates="timeSlotDates"
        :time-slot-start="timeSlotStart"
        :time-slot-duration="timeSlotDuration"
        @update-time-slot-dates="updateTimeSlotDates"
        @update-time-slot-start="updateTimeSlotStart"
        @update-time-slot-duration="updateTimeSlotDuration"
      />
      <v-row v-else no-gutters>
        <v-col>
          <vs-date-picker
            :key="`${jobId}-start-time`"
            data-test="job-start-date"
            :value="startTime"
            label="Start"
            :disabled="isDisabled"
            :can-select-past="canSelectPast"
            :rules="[() => showTimeWarning && 'Start cannot be after end']"
            required
            @input="updateStartTime"
          />
        </v-col>
        <v-col class="pl-2">
          <vs-date-picker
            :key="`${jobId}-end-time`"
            data-test="job-end-date"
            :value="endTime"
            label="End"
            :can-select-past="canSelectPast"
            :disabled="isDisabled"
            required
            @input="updateEndTime"
          />
        </v-col>
      </v-row>
    </vs-wrapper>

    <RepeatJobsSelectorConnector
      v-if="showRepeatJobsSelector"
      :allow-multi-service="activity?.followUpJobsAllowance === FollowUpJobsAllowance.MultiService"
    />
    <job-prediction-details v-if="!isNew" :job-number="jobId" />

    <vs-wrapper space="large">
      <vs-heading type="overline">Details</vs-heading>
      <vs-wrapper space="small">
        <UpdateBanner
          v-if="shouldShowUpdateCustomFieldsBanner"
          @update-schema-requested="updateSchemaButtonClicked"
        />

        <CostCenterField
          data-test="costcenter-selector"
          :value="costCenter"
          :ticket-owner="ownerId"
          :ticket-destination="destinationLocationId"
          :ticket-sources="pickupLocationIds"
          :hub-id="hubId"
          label="Cost center"
          :disabled="isDisabled"
          @input="updateCostCenter"
          @auto-fill="autoFill"
        />

        <vs-text-input
          :value="details"
          data-test="job-additional-details"
          label="Additional details..."
          :disabled="isDisabled"
          multiline
          @input="updateDetails"
        />

        <custom-fields-fetch-progress-indicator
          v-if="loadingExtensionSchema || errorFetchingExtensionSchema"
          :error-fetching-extension-schema="errorFetchingExtensionSchema"
          @retry="retryFetchingExtensionSchema"
        />
        <schema-field
          v-if="!errorFetchingExtensionSchema && !!schema.properties"
          :error-schema="errorSchema"
          :form-data="customFormData"
          :business-unit-id="hubId"
          :params="{ headingType: 'heading' }"
          :root-schema="schema"
          :schema="schema"
          :ui-schema="uiSchema"
          :form-resolver="contextualizedFormResolver"
          :disabled="isDisabled"
          @change="updateFormData"
        />
      </vs-wrapper>
    </vs-wrapper>

    <vs-wrapper>
      <JobTagsDisplay :job-id="isNew ? undefined : jobUuid" />
    </vs-wrapper>

    <job-form
      v-if="activityId"
      :job-form-associations="activity.formAssociations"
      :job-form-submissions="formSubmissions"
      :job-is-approved="jobIsApproved"
      :is-new="isNew"
      @edit-job-form-submission="
        (e) => $emit('edit-form-submission', e.formUuid, e.formSubmissionUuid)
      "
      @fill-job-form-submission="(e) => $emit('fill-form-submission', e.formUuid)"
    />

    <vs-wrapper>
      <assignment-form-requirements
        :form-requirements="assignmentFormRequirements"
        :service-provider-business-unit-id="serviceProviderBusinessUnitId"
        :hub-id="hubId"
        :is-new-job="isNew"
        @fill-form-requirement="fillFormRequirement"
        @view-form-requirement-submission="viewFormRequirementSubmission"
        @update-form-requirements="updateAssignmentFormRequirements"
        @show-assignment-form-selection="showAssignmentFormSelection"
      />
    </vs-wrapper>

    <Attachments
      v-if="areAttachmentsEnabled"
      :job-uuid="jobUuid"
      :client-only="jobId === null"
      :job-attachments="attachments"
      :attachment-storage-id="attachmentsStorageId"
      :enable-delete="canEditAttachments"
      @attachment-removed="handleAttachmentRemoved"
    >
      <template>
        <attachment-card
          v-for="formSubmission in adHocFormSubmissions"
          :key="`formSubmission-${formSubmission.formSubmissionId}`"
          icon="assignment_turned_in"
          :primary-text="formSubmission.formName"
          :secondary-text="getAdhocFormSubmissionSecondaryText(formSubmission)"
        >
          <template #primary-text>
            <a
              class="text-decoration-underline"
              @click.prevent="
                $emit(
                  'show-form-submission',
                  formSubmission.formId,
                  formSubmission.formSubmissionId,
                )
              "
              >{{ formSubmission.formName }}</a
            >
          </template>
        </attachment-card>
      </template>
    </Attachments>

    <vs-wrapper v-if="addFormsEnabled || canEditAttachments" space="small">
      <v-row dense>
        <v-col class="shrink" align-self="center">
          <vs-text class="add-text">Add:</vs-text>
        </v-col>
        <v-col class="shrink" align-self="center">
          <attachment-picker
            v-if="canEditAttachments"
            class="pl-0"
            data-test="add-attachments"
            :job-uuid="jobUuid"
            :job-is-new="jobId === null"
            @attachment-added="handleAttachmentAdded"
            @attachment-updated="handleAttachmentUpdated"
            @unsupported-file-selected="unsupportedFileSelected"
            @error-uploading-attachment="errorUploadingAttachment"
          />
        </v-col>
        <v-divider v-if="canEditAttachments && addFormsEnabled" class="my-3" vertical />
        <v-col class="shrink" align-self="center">
          <vs-button
            v-if="addFormsEnabled"
            icon="mdi-clipboard-text-outline"
            label="Form"
            type="tertiary"
            small
            @click="showFormSelection"
          />
        </v-col>
      </v-row>
    </vs-wrapper>

    <job-references-card v-if="!isNew" :job-id="jobUuid" />

    <line-items-card
      v-if="canViewLineItems"
      :job-id="jobUuid"
      :job-approval-status="approvalStatus"
      show-batch-link
      :readonly="!lineItemsEditPermission.allowed"
      :edit-permission="lineItemsEditPermission"
      :batch-id="batchId"
      :assigned-to-service-provider-id="serviceProviderBusinessUnitId"
      :hub-id="hubId"
      :line-items-when-signed="signature?.metadata.itemsWhenSigned ?? []"
    />

    <job-signature
      v-if="showSignature"
      :job-id="jobUuid"
      :job-status="jobStatus"
      :job-approval-status="approvalStatus"
      :signature="signature"
      @clear-signature="clearSignature"
    />

    <job-activity-log
      v-if="!!jobId"
      :job-id="jobUuid"
      :job-hub-id="hubId"
      :custom-field-definitions="flattenedFormFields"
      :job-number="jobId"
      :start-time="startTime"
      :end-time="endTime"
    />
  </vs-form>
</template>

<script>
import moment from 'moment'
import { isEmpty } from 'lodash'
import { computed } from 'vue'
import { mapGetters } from 'vuex'

import { APPROVED, REQUESTED } from '@/models/job'

import { formatDateTime } from '@/utils/date-formatting'
import { FormContext } from '@/utils/forms/form-context'
import { ContextualizedFormResolver } from '@/utils/forms/contextualized-form-resolver'
import { mergeFormDataWithDefault, resolveFormDataForValidation } from '@/utils/forms/form-resolver'
import { validateFormData } from '@/utils/forms/form-validate'
import { extractFieldsFromSchema } from '@/utils/field-schema-helper'

import CostCenterField from '@/components/common/CostCenterField'
import { useJobsSearch } from '@/components/jobs/search'
import { lineItemSidesheetPermission } from '@/components/jobs/line-items/lineItemActionPermissions'
import LineItemsCard from '@/components/jobs/line-items/LineItemsCard.vue'
import JobSignature from '@/components/jobs/sections/JobSignature.vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsButton from '@/components/vision/VsButton.vue'
import AdHocPool from './AdHocPool.vue'
import AdHocPoolCreator from './AdHocPoolCreator.vue'
import AssignmentFormRequirements from './AssignmentFormRequirements.vue'
import AttachmentCard from './AttachmentCard'
import AttachmentPicker from './AttachmentPicker'
import Attachments from './Attachments'
import CustomFieldsFetchProgressIndicator from './CustomFields/CustomFieldsFetchProgressIndicator.vue'
import PoolStatus from './PoolStatus.vue'
import PrioritySelector from './PrioritySelector'
import TaskItem from './TaskItem.vue'
import TicketFormMultiDayScheduler from './TicketFormMultiDayScheduler'
import RepeatJobsSelectorConnector from './RepeatJobsSelectorConnector.vue'
import UpdateBanner from './CustomFields/UpdateBanner.vue'
import JobActivityLog from '@/components/tickets/history/JobActivityLog.vue'
import JobRequesterPicker from '@/components/tickets/JobRequesterPicker/JobRequesterPicker.vue'
import JobFollowersPicker from '@/components/tickets/JobFollowersPicker/JobFollowersPicker.vue'
import JobReferencesCard from '@/components/jobs/references/JobReferencesCard.vue'
import JobDivisionPicker from '@/components/tickets/JobDivisionPicker.vue'
import JobTagsDisplay from '@/components/jobs/JobTagsDisplay.vue'
import JobPredictionDetails from '@/components/jobs/JobPredictionDetails.vue'
import JobForm from './JobForm.vue'
import { useJobFormDataStore } from '@/components/jobs/jobFormDataStore'
import { JobStopType } from '@ironsight/types'
import { buildRootSchema } from './common/useExtensionSchemaId'
import { useGetAdhocFormSubmissions } from './JobForm'
import RepeatJobsInfoDisplay from './RepeatJobsInfoDisplay.vue'
import { useGetJobRequestQuery } from '@/api/jobs'
import { FollowUpJobsAllowance, StopConfirmationRequirement } from '@/models/activity'
import { getItineraryStops, getStopLocationId, getStopLocationIds } from './jobStopLocations'

export default {
  name: 'TicketForm',
  components: {
    JobPredictionDetails,
    JobTagsDisplay,
    JobDivisionPicker,
    JobReferencesCard,
    JobFollowersPicker,
    JobRequesterPicker,
    JobSignature,
    VsButton,
    VsWrapper,
    LineItemsCard,
    AssignmentFormRequirements,
    CostCenterField,
    PrioritySelector,
    Attachments,
    AttachmentPicker,
    TicketFormMultiDayScheduler,
    RepeatJobsSelectorConnector,
    RepeatJobsInfoDisplay,
    TaskItem,
    AttachmentCard,
    JobActivityLog,
    PoolStatus,
    AdHocPool,
    AdHocPoolCreator,
    CustomFieldsFetchProgressIndicator,
    UpdateBanner,
    JobForm,
  },
  props: {
    jobId: {
      type: Number,
      default: null,
    },
    jobUuid: {
      type: String,
      default: null,
    },
    jobRequestId: {
      type: String,
      default: null,
    },
    unitWorkingStatus: {
      type: String,
      default: () => '',
    },
    activeDuration: {
      type: Object,
      default: null,
    },
    priorityId: {
      type: Number,
      default: null,
    },
    ownerId: {
      type: String,
      default: null,
    },
    accountId: {
      type: String,
      default: null,
    },
    customerSid: {
      type: String,
      default: null,
    },
    followerIds: {
      type: Array,
      default: () => [],
    },
    followerSidsById: {
      type: Object,
      default: () => ({}),
    },
    startTime: {
      type: Object,
      default: null,
    },
    endTime: {
      type: Object,
      default: null,
    },
    activityId: {
      type: Number,
      default: null,
    },
    resourceTypeId: {
      type: Number,
      default: null,
    },
    crew: {
      type: Array,
      default: () => [],
    },
    unitId: {
      type: Number,
      default: null,
    },
    serviceProviderBusinessUnitId: {
      type: String,
      default: null,
    },
    costCenter: {
      type: String,
      default: () => '',
    },
    details: {
      type: String,
      default: () => '',
    },
    attachmentsStorageId: {
      type: String,
      default: null,
    },
    customFormData: {
      type: Object,
      default: () => {},
    },
    attachments: {
      type: Array,
      default: () => [],
    },
    isFetching: Boolean,
    errorFetchingExtensionSchema: {
      type: Object,
      default: null,
    },
    extensionSchemaId: {
      type: Number,
      default: null,
    },
    extensionSchema: {
      type: Object,
      default: () => {},
    },
    hasNewerCustomFieldsSchema: {
      type: Boolean,
      default: false,
    },
    timeSlotDates: {
      type: Array[String],
      default: [],
    },
    timeSlotStart: {
      type: String,
      default: '',
    },
    timeSlotDuration: {
      type: Number,
      default: 0,
    },
    bulkCreate: {
      type: Boolean,
      default: false,
    },
    formSubmissions: {
      type: Array,
      default: () => [],
    },
    approvalStatus: {
      type: String,
      default: () => '',
    },
    jobStatus: {
      type: String,
      default: () => '',
    },
    hubId: {
      type: String,
      required: true,
    },
    isReadonly: {
      type: Boolean,
    },
    isCancelled: {
      type: Boolean,
      default: false,
    },
    isNew: {
      type: Boolean,
    },
    assignmentGroupNumber: {
      type: Number,
      default: null,
    },
    poolId: {
      type: String,
      default: null,
    },
    assignmentFormRequirements: {
      type: Array,
      default: () => [],
    },
    batchId: {
      type: String,
      default: () => '',
    },
    signature: {
      type: Object,
      default: () => {},
    },
    showRepeatedJobWarningBanner: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const jobFormDataStore = useJobFormDataStore()
    const destinationLocationId = computed(() => {
      return getStopLocationId(jobFormDataStore.requestedStops, JobStopType.DropOff)
    })
    const pickupLocationIds = computed(() => {
      return getStopLocationIds(jobFormDataStore.requestedStops, JobStopType.PickUp)
    })
    const query = useJobsSearch(
      {
        assignmentGroupNumber: props.assignmentGroupNumber,
        sortBy: ['number'],
        sortDesc: [true],
        offset: 0,
        limit: 10,
      },
      { enabled: !!props.assignmentGroupNumber },
    )

    const itineraries = computed(() => {
      return [
        ...(query.jobs.value
          ?.filter((j) => j.number !== props.jobId)
          .map((j) => ({
            job_id: j.id,
            startTime: moment(j.requested_start),
            endTime: moment(j.requested_end),
            extensionData: j.extension_data,
            stops: [
              ...j.inventory_requirements.map((i) => ({
                locationId: i.pickup_location_id,
                order: i.position,
                items: [],
              })),
              {
                locationId: j.destination_id,
                items: [],
                order: j.inventory_requirements.length,
              },
            ],
          })) || []),
        {
          job_id: props.jobUuid,
          startTime: props.startTime,
          endTime: props.endTime,
          extensionData: props.customFormData,
          stops: getItineraryStops(jobFormDataStore.requestedStops),
        },
      ]
    })

    const { getAdhocFormSubmissionsWithFormNames } = useGetAdhocFormSubmissions()

    const jobRequestIdRef = computed(() => props.jobRequestId)
    const { data: jobRequest } = useGetJobRequestQuery(jobRequestIdRef)
    const isPrimaryJobOfJobRequest = computed(() => {
      return !props.jobRequestId || jobRequest.value?.primaryJobId === props.jobUuid
    })
    const jobRequestIsRepeatTrip = computed(() => {
      return jobRequest.value?.isRepeatTrip ?? null
    })

    return {
      itineraries,
      destinationLocationId,
      pickupLocationIds,
      getAdhocFormSubmissionsWithFormNames,
      jobRequest,
      isPrimaryJobOfJobRequest,
      jobRequestIsRepeatTrip,
    }
  },
  data() {
    return {
      adHocPool: {
        stages: [{ stageNumber: 1, recipients: [] }],
        delay: null,
      },
      errorSchema: {},
      isCreatingAdHocPool: false,
      poolDefinitionId: null,
    }
  },

  computed: {
    FollowUpJobsAllowance() {
      return FollowUpJobsAllowance
    },
    StopConfirmationRequirement() {
      return StopConfirmationRequirement
    },
    ...mapGetters({
      getActivityById: 'activities/getById',
      getDivisionByUuid: 'divisions/getByUuid',
      getTaskById: 'tasks/getById',
      getFormSubmissionById: 'formSubmissions/getById',
      hasAnyVisibleForms: 'forms/getHasAnyVisibleForms',
      getUserBySid: 'users/getBySid',
      getBusinessUnitById: 'businessUnits/getBusinessUnitById',
      lineItemsFeatureFlag: 'featureFlags/lineItems',
      getJobCustomFieldsSchema: 'schemas/getJobCustomFieldsSchema',
      canScheduleJobsInPast: 'permissions/canScheduleJobsInPast',
    }),
    addFormsEnabled() {
      return this.hasAnyVisibleForms && !this.isNew
    },
    loadingExtensionSchema() {
      return !!(
        this.extensionSchemaId &&
        Object.keys(this.extensionSchema).length === 0 &&
        !this.isNew
      )
    },
    combinedSchema() {
      return buildRootSchema(
        this.getDivisionByUuid(this.ownerId),
        this.selectedTask,
        this.getJobCustomFieldsSchema,
        this.extensionSchemaId,
        this.extensionSchema,
      )
    },
    schema() {
      return this.combinedSchema.schema
    },
    uiSchema() {
      return this.combinedSchema.uiSchema
    },
    flattenedFormFields() {
      if (!this.schema) {
        return {}
      }
      const formFields = {}
      extractFieldsFromSchema(this.schema, this.uiSchema ?? {}, formFields)
      return formFields
    },
    defaultCustomData() {
      // getDefaultFormData recurses, traverses 1st arg while keeping the 2nd
      // arg (the "root") the same.
      return this.contextualizedFormResolver.getDefaultFormData(
        this.schema,
        this.schema,
        this.customFormData,
        undefined,
      )
    },
    showRepeatJobsSelector() {
      return (
        this.activity !== null &&
        this.activity?.followUpJobsAllowance !== FollowUpJobsAllowance.NotAllowed &&
        this.isPrimaryJobOfJobRequest
      )
    },
    shouldShowUpdateCustomFieldsBanner() {
      return !this.isDisabled && this.extensionSchemaId !== null && this.hasNewerCustomFieldsSchema
    },
    selectedTask() {
      const activity = this.getActivityById(this.activityId)
      if (!activity) {
        return null
      }
      return this.getTaskById(activity.taskId)
    },
    activity() {
      return this.getActivityById(this.activityId)
    },
    canSelectPast() {
      return this.canScheduleJobsInPast || !!this.jobId
    },
    showTimeWarning() {
      return this.endTime.isBefore(this.startTime)
    },
    areAttachmentsEnabled() {
      return !(this.bulkCreate && this.timeSlotDates.length > 1)
    },
    canEditAttachments() {
      if (!this.areAttachmentsEnabled) {
        return false
      }

      return !this.isDisabled
    },
    lineItemsEditPermission() {
      return lineItemSidesheetPermission(
        this.serviceProviderBusinessUnitId,
        this.$auth.organizationId,
      )
    },
    showBulkCreateIcon() {
      return !this.jobId
    },
    cannotCloseBulkCreate() {
      return this.bulkCreate && this.timeSlotDates.length > 0
    },
    cannotOpenBulkCreate() {
      return !this.bulkCreate && this.attachments.length > 0
    },

    contextualizedFormResolver() {
      const hub = this.getBusinessUnitById(this.hubId)
      const currentUser = this.getUserBySid(this.$auth.user.sub) || this.$auth.user

      const context = new FormContext({
        hub,
        currentUser,
        additionalContext: this.additionalContext,
      })
      return new ContextualizedFormResolver(context)
    },
    isDisabled() {
      return this.isReadonly || this.isCancelled
    },
    showSignature() {
      return !this.isNew && this.jobStatus !== REQUESTED
    },
    canViewLineItems() {
      if (!this.lineItemsFeatureFlag) return false
      if (this.isCancelled || this.isNew) return false
      if (this.serviceProviderBusinessUnitId === null || this.jobStatus === REQUESTED) return false

      const hub = this.getBusinessUnitById(this.hubId)
      const isAssignedInternally =
        !!hub?.ownerId && this.serviceProviderBusinessUnitId === hub.ownerId

      return !isAssignedInternally
    },
    jobIsApproved() {
      return this.approvalStatus === APPROVED
    },
    adHocFormSubmissions() {
      if (!this.activity) {
        return []
      }
      return this.getAdhocFormSubmissionsWithFormNames(
        this.formSubmissions,
        this.activity.formAssociations,
      )
    },
  },
  watch: {
    schema: function (newSchema) {
      if (newSchema.properties) {
        this.$emit(
          'update:customFormData',
          mergeFormDataWithDefault(this.customFormData, this.defaultCustomData),
        )
      }
    },
  },
  created() {
    if (this.schema.properties) {
      this.$emit(
        'update:customFormData',
        mergeFormDataWithDefault(this.customFormData, this.defaultCustomData),
      )
    }
  },
  methods: {
    validate(jobStatus) {
      const reopeningCancelledJobWithOutdatedSchema =
        this.status === 'Cancelled' && jobStatus !== 'Cancelled' && this.hasNewerCustomFieldsSchema

      if (reopeningCancelledJobWithOutdatedSchema) {
        return this.$refs.form.isValid() && this.$refs['task-item'].validate()
      }

      return (
        this.validateCustomFields() &&
        this.$refs.form.isValid() &&
        this.$refs['task-item'].validate()
      )
    },
    validateCustomFields() {
      const resolvedSchema = this.contextualizedFormResolver.retrieveSchema(
        this.schema,
        this.schema,
        this.customFormData,
      )
      const resolvedData = resolveFormDataForValidation(this.customFormData)

      const errorSchema = validateFormData(resolvedSchema, resolvedData)
      this.errorSchema = errorSchema

      return isEmpty(errorSchema)
    },
    /**
     * `update:extension-schema-id` is insufficient here because we want
     * Ticket.vue to show snackbar warning.
     */
    updateSchemaButtonClicked() {
      this.$emit('update-schema-button-clicked')
    },
    updateStartTime(newValue) {
      this.$emit('update-start-time', newValue)
    },
    removeJobFromGroup() {
      this.$emit('remove-job-from-group')
    },
    updateEndTime(value) {
      this.$emit('update:endTime', value)
    },
    updateCostCenter(value) {
      this.$emit('update:costCenter', value)
    },
    updatePriority(id) {
      this.$emit('update:priorityId', id)
    },
    updateActivity(value) {
      const activity = this.getActivityById(value)

      if (activity) {
        const task = this.getTaskById(activity.taskId)
        this.$emit('update:extension-schema-id', task.version)
      }

      this.$emit('update:activityId', value)
    },
    updateCrew(crew) {
      this.$emit('update:crew', crew)
    },
    updateUnit(unitId) {
      this.$emit('update:unitId', unitId)
    },
    updateServiceProviderBusinessUnitId(serviceProviderBusinessUnitId) {
      if (
        serviceProviderBusinessUnitId !== null &&
        serviceProviderBusinessUnitId !== this.serviceProviderBusinessUnitId
      ) {
        this.updateAssignmentFormRequirements([])
      }
      this.$emit('update:serviceProviderBusinessUnitId', serviceProviderBusinessUnitId)
    },
    updateDetails(value) {
      this.$emit('update:details', value)
    },
    updateFormData(value) {
      this.$emit(
        'update:customFormData',
        mergeFormDataWithDefault(
          value,
          this.contextualizedFormResolver.getDefaultFormData(
            this.schema,
            this.schema,
            value,
            undefined,
          ),
        ),
      )
    },
    updateAssignmentFormRequirements(requirements) {
      this.$emit('update:assignment-form-requirements', requirements)
    },
    fillFormRequirement(value) {
      this.$emit('fill-form-requirement', value)
    },
    viewFormRequirementSubmission(value) {
      this.$emit('view-form-requirement-submission', value)
    },
    showAssignmentFormSelection() {
      this.$emit('show-assignment-form-selection')
    },
    handleAttachmentAdded(value) {
      this.$emit('update:attachments', [...this.attachments, value])
    },
    handleAttachmentUpdated(value) {
      this.$emit('update:attachments', [
        ...this.attachments.filter((a) => a.id !== value.id),
        value,
      ])
    },
    handleAttachmentRemoved(value) {
      this.$emit(
        'update:attachments',
        this.attachments.filter((a) => a.id !== value),
      )
    },
    unsupportedFileSelected(messageKey) {
      this.$emit('unsupported-file-selected', messageKey)
    },
    errorUploadingAttachment(messageKey) {
      this.$emit('error-uploading-attachment', messageKey)
    },
    toggleBulkCreate() {
      this.$emit('update:bulkCreate', !this.bulkCreate)
    },
    updateTimeSlotDates(value) {
      this.$emit('update:timeSlotDates', value)
    },
    updateTimeSlotStart(value) {
      this.$emit('update:timeSlotStart', value)
    },
    updateTimeSlotDuration(value) {
      this.$emit('update:timeSlotDuration', value)
    },
    showFormSelection() {
      this.$emit('show-form-selection')
    },
    retryFetchingExtensionSchema() {
      this.$emit('retry-custom-schema-fetch')
    },
    getAdhocFormSubmissionSecondaryText(formSubmission) {
      const userName = this.getUserBySid(formSubmission.submittedBy)?.name
      const uploadedTime = formatDateTime(formSubmission.submittedOn)

      return userName ? `${userName} on ${uploadedTime}` : `${uploadedTime}`
    },
    clearSignature() {
      this.$emit('clear-signature')
    },
    autoFill(field, value) {
      this.$emit('auto-fill', field, value)
    },
    handleDiscard() {
      this.poolDefinitionId = null
      this.isCreatingAdHocPool = false
    },
    onPoolClosed() {
      this.$emit('pool:closed')
    },
    assignToPool(id) {
      this.poolDefinitionId = id
      this.$emit('clear-assigned-pool')
      if (id == null) {
        this.updatePoolExecutionPlan(null)
      } else {
        this.updateAssignmentFormRequirements([])
      }
    },
    updatePoolExecutionPlan(value) {
      this.$emit('update-pool-execution-plan', value)
    },
    createAdHocPool(value) {
      if (value === true) this.updateAssignmentFormRequirements([])
      this.isCreatingAdHocPool = value
      this.adHocPool = {
        stages: [{ stageNumber: 1, recipients: [] }],
        delay: null,
      }
    },
    updateAdHocPoolDelay(value) {
      this.adHocPool.delay = value
      this.updatePoolExecutionPlanWithAdHocPool()
    },
    addAdHocPoolStage(value) {
      this.adHocPool.stages.push({
        stageNumber: value,
        recipients: [],
      })
      this.updatePoolExecutionPlanWithAdHocPool()
    },
    updateAdHocPoolStage(value) {
      this.adHocPool.stages.splice(value.stageNumber - 1, 1, value)
      this.updatePoolExecutionPlanWithAdHocPool()
    },
    removeAdHocPoolStage(stageNumber) {
      if (this.adHocPool.stages.length === 1) {
        this.isCreatingAdHocPool = false
        return
      }

      this.adHocPool.stages.splice(stageNumber - 1, 1)

      this.adHocPool.stages.forEach((s) => {
        if (s.stageNumber > stageNumber) {
          s.stageNumber = s.stageNumber - 1
        }
      })
      this.updatePoolExecutionPlanWithAdHocPool()
    },
    updatePoolExecutionPlanWithAdHocPool() {
      let delay = null

      if (this.adHocPool.delay) {
        const minutes = parseInt(this.adHocPool.delay)
        const days = Math.floor(minutes / (60 * 24))
        const hours = Math.floor((minutes % (60 * 24)) / 60)
        const remainingMinutes = minutes % 60

        delay = `${days}:${hours}:${remainingMinutes}:00`
      }

      const stages = this.adHocPool.stages.map((s) => ({
        delay,
        stage_number: s.stageNumber,
        recipients: s.recipients.map((r) => ({
          recipient_id: r,
        })),
      }))
      const executionPlan = { stages }
      this.updatePoolExecutionPlan(executionPlan)
    },
  },
}
</script>

<style scoped>
.grid {
  display: flex;
  flex-wrap: wrap;
  margin: 0 calc(var(--space-smaller) * -1) calc(var(--space-small) * -1);
}

.grid-item {
  flex: 1;
  min-width: 50%;
  padding: 0 var(--space-smaller) var(--space-small);
}

.add-text {
  font-family: Montserrat, sans-serif;
  font-weight: bold;
  color: var(--color-grey--light);
}

.progress-center {
  height: 100%;
  width: 100%;
  display: flex;
}
</style>
